import React, { useCallback, useRef, useState } from "react";
import { AlertTriangle, Info } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { PineRequirementsComparisonTable } from "./PineRequirementsComparisonTable";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "./StrategyPineSectionCard";
import type {
  PineRequirementsModalBlock,
  ReportStrategyPineFields,
  ReportStrategyPineRequirementsResponse,
} from "../../types/reportStrategyPine";
import {
  coerceUxCopy,
  getExitHookReadinessDisplay,
  humanizePineUserCopy,
} from "../../utils/pineIrUi";
import { acknowledgeTvEducation, hasAcknowledgedTvEducation } from "../../utils/reportStrategyPineUi";

export const COMPOSE_EDUCATION_STEP_TITLE = "Before you compose for TradingView";
export const COMPOSE_EDUCATION_STEP_DESCRIPTION =
  "Quick overview of what Tessera can and cannot run on your TradingView chart.";

const SIM_TV_SUMMARY =
  "Reports and simulations in Tessera apply your policy automatically (stops, targets, breakeven, trailing, time exits). " +
  "Composed TradingView Pinescript only runs what Pinescript can execute on the chart. Some rules become code; others are labels or comments that do not change orders. " +
  "The first time you compose or copy Pinescript for this strategy, we show a short checklist so you know what to expect.";

const FALLBACK_SIM_TV: PineRequirementsModalBlock = {
  title: "Tessera simulator vs TradingView",
  body:
    "Tessera and TradingView are two different runtimes. Use Tessera to validate policy; use composed Pinescript to trade or visualize on TradingView, with the limits below.",
  bullets: [
    "In Tessera (reports and policy simulator), management rules run as part of backtests and simulations: stops, targets, breakeven, trailing, and time-based exits are applied for you.",
    "On TradingView, only Pinescript on the chart runs. Compose maps policy into Pinescript where possible: entry gating, and patching your exit orders when your script exposes named stop/limit variables.",
    "Rules Tessera cannot execute in Pinescript (e.g. breakeven, trailing, time-based exits) appear as on-chart labels or comments. They show when Tessera would act, but they do not move stops or place orders on TradingView.",
  ],
};

const FALLBACK_EXIT_HOOKS: PineRequirementsModalBlock = {
  title: "Exit management hooks",
  bullets: [
    "For the best compose results, use named stop= and limit= variables in your exit orders (e.g. sl_price, tp_price).",
    "Optional marker in your master Pinescript: // @tessera:exit_vars sl_price tp_price",
  ],
};

const FALLBACK_MGMT: PineRequirementsModalBlock = {
  title: "Policy management: Tessera vs TradingView",
  tables: [
    {
      columns: ["action", "dashboard_sim", "tradingview_compose"],
      rows: [
        {
          action: "Stop / take-profit distance overrides",
          dashboard_sim: "Applied",
          tradingview_compose: "Patches stop/limit variables when hooks are detected",
        },
        {
          action: "Breakeven, trailing stops, time-based exits",
          dashboard_sim: "Applied",
          tradingview_compose: "Labels and comments only (not executed in Pinescript yet)",
        },
      ],
    },
  ],
};

function humanizeTableCell(value: string): string {
  return humanizePineUserCopy(value);
}

function EducationBlockContent({ block }: { block: PineRequirementsModalBlock }): React.ReactElement {
  return (
    <div className="font-[family-name:var(--font-primary)] text-sm text-gray-500 dark:text-white">
      {block.body ? (
        <p className="mb-2 leading-5">{coerceUxCopy(block.body) ?? humanizePineUserCopy(block.body)}</p>
      ) : null}
      {Array.isArray(block.bullets) && block.bullets.length > 0 ? (
        <ul className="mb-3 list-disc space-y-2 pl-4 leading-5">
          {block.bullets.map((b, i) => (
            <li key={i}>
              {typeof b === "string"
                ? coerceUxCopy(b) ?? humanizePineUserCopy(b)
                : coerceUxCopy(b) ?? String(b)}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function PolicyManagementTablesBlock({ block }: { block: PineRequirementsModalBlock }): React.ReactElement {
  const tables = block.tables ?? [];
  const hasProse = Boolean(block.body) || Boolean(block.bullets?.length);

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {hasProse ? <EducationBlockContent block={block} /> : null}
      {tables.map((table, idx) => (
        <PineRequirementsComparisonTable
          key={idx}
          table={{
            ...table,
            rows: (table.rows ?? []).map((row) =>
              Object.fromEntries(
                Object.entries(row).map(([key, value]) => [
                  key,
                  typeof value === "string" ? humanizeTableCell(value) : value,
                ])
              )
            ),
          }}
        />
      ))}
    </div>
  );
}

export type StrategyPineComposeEducationStepProps = {
  simTv: PineRequirementsModalBlock;
  exitHooks: PineRequirementsModalBlock;
  mgmt: PineRequirementsModalBlock;
  dontShowAgain: boolean;
  onDontShowAgainChange: (checked: boolean) => void;
};

/** Step body shown inside the same shell as checklist / compose (not a stacked dialog). */
export function StrategyPineComposeEducationStep({
  simTv,
  exitHooks,
  mgmt,
  dontShowAgain,
  onDontShowAgainChange,
}: StrategyPineComposeEducationStepProps): React.ReactElement {
  const simTvTitle = coerceUxCopy(simTv.title) ?? simTv.title ?? "Tessera simulator vs TradingView";
  const exitHooksTitle = coerceUxCopy(exitHooks.title) ?? exitHooks.title ?? "Exit management hooks";

  return (
    <div data-strategy-compose-education-step="" className="flex min-w-0 flex-col gap-4">
      <div>
        <p className={cn(STRATEGY_PINE_SECTION_LABEL_CLASS, "mb-2 text-sm")}>{simTvTitle}</p>
        <EducationBlockContent block={simTv} />
      </div>

      <div>
        <p className={cn(STRATEGY_PINE_SECTION_LABEL_CLASS, "mb-2 text-sm")}>{exitHooksTitle}</p>
        <EducationBlockContent block={exitHooks} />
      </div>

      <PolicyManagementTablesBlock block={mgmt} />

      <Field orientation="horizontal" className="items-start gap-2">
        <Checkbox
          id="strategy-compose-education-dont-show"
          checked={dontShowAgain}
          onCheckedChange={(checked) => onDontShowAgainChange(checked === true)}
          className="mt-0.5"
        />
        <FieldLabel
          htmlFor="strategy-compose-education-dont-show"
          className={cn(STRATEGY_PINE_SECTION_DESC_CLASS, "cursor-pointer font-normal")}
        >
          Don&apos;t show again for this strategy
        </FieldLabel>
      </Field>
    </div>
  );
}

export type UseStrategyPineEducationGateArgs = {
  strategyId: string;
  fields: ReportStrategyPineFields | null | undefined;
  requirements: ReportStrategyPineRequirementsResponse | null;
  /** Swap the parent shell to the education step (sequential flow, not overlay). */
  onShowEducationStep?: () => void;
};

export function useStrategyPineEducationGate({
  strategyId,
  fields,
  requirements,
  onShowEducationStep,
}: UseStrategyPineEducationGateArgs): {
  runGated: (action: () => void | Promise<void>) => void;
  educationInfoAlerts: React.ReactElement;
  simTv: PineRequirementsModalBlock;
  exitHooks: PineRequirementsModalBlock;
  mgmt: PineRequirementsModalBlock;
  dontShowAgain: boolean;
  setDontShowAgain: (checked: boolean) => void;
  cancelEducationStep: () => void;
  confirmEducationStep: () => void;
} {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const pendingActionRef = useRef<(() => void | Promise<void>) | null>(null);

  const simTv = requirements?.modals?.sim_tv_parity ?? FALLBACK_SIM_TV;
  const exitHooks = requirements?.modals?.exit_hooks ?? FALLBACK_EXIT_HOOKS;
  const mgmt = requirements?.modals?.mgmt_labels ?? FALLBACK_MGMT;

  const simTvTitle = coerceUxCopy(simTv.title) ?? simTv.title ?? "Tessera simulator vs TradingView";
  const simTvSummary =
    coerceUxCopy(simTv.body) ??
    (Array.isArray(simTv.bullets) && simTv.bullets.length > 0
      ? coerceUxCopy(simTv.bullets[0]) ?? SIM_TV_SUMMARY
      : SIM_TV_SUMMARY);

  const exitPartial =
    fields?.exit_hook_readiness === "partial" || fields?.exit_hook_readiness === "unsupported";
  const exitHookUx = getExitHookReadinessDisplay(
    fields?.exit_hook_readiness,
    requirements?.exit_hook_readiness_ux
  );

  const runGated = useCallback(
    (action: () => void | Promise<void>) => {
      if (hasAcknowledgedTvEducation(strategyId)) {
        void Promise.resolve(action());
        return;
      }
      pendingActionRef.current = action;
      setDontShowAgain(false);
      onShowEducationStep?.();
    },
    [onShowEducationStep, strategyId]
  );

  const cancelEducationStep = useCallback(() => {
    pendingActionRef.current = null;
  }, []);

  const confirmEducationStep = useCallback(() => {
    if (dontShowAgain) acknowledgeTvEducation(strategyId);
    const fn = pendingActionRef.current;
    pendingActionRef.current = null;
    if (fn) void Promise.resolve(fn());
  }, [dontShowAgain, strategyId]);

  const educationInfoAlerts = (
    <div className="mb-4 flex flex-col gap-2" data-strategy-compose-education-alerts="">
      <Alert variant="info" className="rounded-lg font-[family-name:var(--font-primary)]">
        <Info aria-hidden />
        <AlertTitle className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{simTvTitle}</AlertTitle>
        <AlertDescription className="text-sm text-gray-500 dark:text-white">{simTvSummary}</AlertDescription>
      </Alert>

      {exitPartial ? (
        <Alert variant="warning" className="rounded-lg font-[family-name:var(--font-primary)]">
          <AlertTriangle aria-hidden />
          <AlertTitle className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
            {coerceUxCopy(exitHooks.title) ?? exitHooks.title}
          </AlertTitle>
          <AlertDescription className="text-sm text-gray-500 dark:text-white">
            {exitHookUx?.description ??
              "Tessera could not fully detect exit hooks in your master Pinescript. Compose may add labels only instead of patching your exit orders."}{" "}
            Use named <span className="font-mono">stop</span> / <span className="font-mono">limit</span>{" "}
            variables (or the <span className="font-mono">@tessera:exit_vars</span> marker) for stronger
            results.
          </AlertDescription>
        </Alert>
      ) : null}

      <PolicyManagementTablesBlock block={mgmt} />
    </div>
  );

  return {
    runGated,
    educationInfoAlerts,
    simTv,
    exitHooks,
    mgmt,
    dontShowAgain,
    setDontShowAgain,
    cancelEducationStep,
    confirmEducationStep,
  };
}

export type ComposeEducationFooterProps = {
  onBack: () => void;
  onConfirm: () => void;
};

export function ComposeEducationFooter({
  onBack,
  onConfirm,
}: ComposeEducationFooterProps): React.ReactElement {
  return (
    <>
      <Button
        type="button"
        variant="outline-gray"
        size="sm"
        data-font="primary"
        data-strategy-compose-education-back=""
        className="h-8 shrink-0 px-3 normal-case tracking-normal"
        onClick={onBack}
      >
        Back
      </Button>
      <Button
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        data-strategy-compose-education-confirm=""
        className="h-8 shrink-0 px-3 normal-case tracking-normal"
        onClick={onConfirm}
      >
        I understand, continue
      </Button>
    </>
  );
}
